import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useRef, useState } from 'react';
import { SearchRecommendation } from './SearchRecommendation';
import { CoreDomainSearchModule } from '@wix/core-domain-search';
import { SearchRecommendationModule } from './SearchRecommendation.module';
import { isInteractiveClickTarget, nextPageBoundaries, pageOfIndex } from './utils';
import s from './SearchRecommendation.module.scss';
export const SearchRecommendationList = _ref => {
  var _CoreDomainSearchModu;
  let items = _ref.items,
    dataHook = _ref.dataHook,
    onClickGetIt = _ref.onClickGetIt,
    onItemClick = _ref.onItemClick,
    onAiInsightsClick = _ref.onAiInsightsClick,
    onHoverChange = _ref.onHoverChange,
    isLoading = _ref.isLoading,
    disabled = _ref.disabled,
    _ref$firstSuggestionP = _ref.firstSuggestionPrimary,
    firstSuggestionPrimary = _ref$firstSuggestionP === void 0 ? true : _ref$firstSuggestionP,
    _ref$primaryBadgeOnly = _ref.primaryBadgeOnlyForFirst,
    primaryBadgeOnlyForFirst = _ref$primaryBadgeOnly === void 0 ? false : _ref$primaryBadgeOnly,
    _ref$useMobileLayout = _ref.useMobileLayout,
    useMobileLayout = _ref$useMobileLayout === void 0 ? false : _ref$useMobileLayout,
    _ref$defaultNonHovere = _ref.defaultNonHovered,
    defaultNonHovered = _ref$defaultNonHovere === void 0 ? false : _ref$defaultNonHovere,
    _ref$hasActiveRow = _ref.hasActiveRow,
    hasActiveRow = _ref$hasActiveRow === void 0 ? true : _ref$hasActiveRow,
    _ref$isLeanView = _ref.isLeanView,
    isLeanView = _ref$isLeanView === void 0 ? false : _ref$isLeanView,
    _ref$useCompactSugges = _ref.useCompactSuggestionRowStyle,
    useCompactSuggestionRowStyle = _ref$useCompactSugges === void 0 ? false : _ref$useCompactSugges,
    pricingOptimized = _ref.pricingOptimized,
    _ref$isPricingLoading = _ref.isPricingLoading,
    isPricingLoading = _ref$isPricingLoading === void 0 ? false : _ref$isPricingLoading,
    _ref$renderLastDivide = _ref.renderLastDivider,
    renderLastDivider = _ref$renderLastDivide === void 0 ? false : _ref$renderLastDivide,
    _ref$compactPriceLabe = _ref.compactPriceLabel,
    compactPriceLabel = _ref$compactPriceLabe === void 0 ? false : _ref$compactPriceLabe,
    titleOverflowStrategy = _ref.titleOverflowStrategy,
    _ref$positionOffset = _ref.positionOffset,
    positionOffset = _ref$positionOffset === void 0 ? 0 : _ref$positionOffset,
    _ref$reportImpression = _ref.reportImpressions,
    reportImpressions = _ref$reportImpression === void 0 ? false : _ref$reportImpression;
  const _useState = useState(0),
    hoveredIndex = _useState[0],
    setHoveredIndex = _useState[1];
  const _useState2 = useState(false),
    hasHovered = _useState2[0],
    setHasHovered = _useState2[1];
  const _SearchRecommendation = SearchRecommendationModule.SDK.Layouts,
    SearchRecommendationListContainer = _SearchRecommendation.SearchRecommendationListContainer,
    Divider = _SearchRecommendation.Divider,
    HoverBox = _SearchRecommendation.HoverBox,
    Skeleton = _SearchRecommendation.Skeleton;
  useEffect(() => {
    onHoverChange == null || onHoverChange(hoveredIndex);
  }, [hoveredIndex, onHoverChange]);
  const searchKey = (_CoreDomainSearchModu = CoreDomainSearchModule.SDK.domainInputStore.useSubmittedFilters()) == null ? void 0 : _CoreDomainSearchModu.query;
  const pageBoundariesRef = useRef([]);
  const pagedSearchKeyRef = useRef(searchKey);
  const isNewSearch = pagedSearchKeyRef.current !== searchKey;
  pagedSearchKeyRef.current = searchKey;
  pageBoundariesRef.current = nextPageBoundaries(pageBoundariesRef.current, items.length, isNewSearch);
  const pageOf = index => pageOfIndex(pageBoundariesRef.current, index);
  const skeleton = isLoading ? /*#__PURE__*/React.createElement(Skeleton, {
    useMobileLayout: useMobileLayout,
    defaultNonHovered: useCompactSuggestionRowStyle,
    optimized: useCompactSuggestionRowStyle || isLeanView
  }) : null;
  if (isLoading && items.length === 0) {
    return skeleton;
  }
  const rows = items.map((item, index) => {
    const isActive = hasActiveRow && index === hoveredIndex;
    // With defaultNonHovered, suppress the initial .hovered styling on the first
    // card — keep the primary button skin via isActive, but only show the
    // hover lift/shadow once the user has actually hovered a card.
    const isHovered = isActive && (!defaultNonHovered || hasHovered);
    const isNextHovered = hasActiveRow && index + 1 === hoveredIndex && (!defaultNonHovered || hasHovered);
    const isNotLast = index < items.length - 1;
    const shouldHideDivider = (isHovered || isNextHovered) && !useCompactSuggestionRowStyle;
    const divider = (isNotLast || renderLastDivider || !!skeleton) && /*#__PURE__*/React.createElement(Divider, {
      className: `${s.divider}${shouldHideDivider ? ` ${s.hidden}` : ''}`
    });
    const row = /*#__PURE__*/React.createElement(SearchRecommendation, _extends({}, item, {
      primary: firstSuggestionPrimary && index === 0,
      primaryBadgeOnly: primaryBadgeOnlyForFirst && index === 0,
      active: useCompactSuggestionRowStyle ? isHovered : isActive,
      onButtonClick: onClickGetIt,
      onAiInsightsClick: onAiInsightsClick,
      isLeanView: isLeanView,
      useCompactSuggestionRowStyle: useCompactSuggestionRowStyle,
      pricingOptimized: pricingOptimized,
      isPricingLoading: isPricingLoading,
      compactPriceLabel: compactPriceLabel,
      titleOverflowStrategy: titleOverflowStrategy,
      showLabelBadge: useCompactSuggestionRowStyle ? false : !isLeanView || index === 0,
      onMouseEnter: useMobileLayout && !defaultNonHovered ? undefined : () => {
        setHoveredIndex(index);
        setHasHovered(true);
      },
      dataHook: `${dataHook}-item-${index}`,
      disabled: disabled,
      useMobileLayout: useMobileLayout,
      impressionReport: reportImpressions ? {
        position: positionOffset + index + 1,
        pageNumber: pageOf(index)
      } : undefined
    }));
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: item.id
    }, /*#__PURE__*/React.createElement(HoverBox, {
      display: "block",
      className: [s.searchRecommendation, useCompactSuggestionRowStyle && s.optimized, isHovered && s.hovered].filter(Boolean).join(' ')
    }, onItemClick ? /*#__PURE__*/React.createElement("div", {
      className: s.cardClickArea,
      "data-hook": `${dataHook}-item-${index}-click-area`,
      onClick: event => {
        if (disabled || isInteractiveClickTarget(event.target)) {
          return;
        }
        onItemClick(item);
      }
    }, row) : row), divider);
  });
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(SearchRecommendationListContainer, {
    dataHook: dataHook,
    className: s.searchRecommendationList
  }, rows, skeleton));
};
//# sourceMappingURL=SearchRecommendationList.js.map